/**
 * Stable snake_case error codes and their HTTP statuses (docs/specs/api-conventions.md §Errors).
 */

export const ERROR_CODES_BY_STATUS = {
  400: ['bad_request', 'invalid_cursor', 'invalid_json'],
  401: [
    'unauthenticated',
    'session_expired',
    'invalid_credentials',
    'invalid_token',
    'token_expired',
    'share_password_required',
    'share_password_invalid',
    'email_not_verified',
  ],
  403: [
    'permission_denied',
    'owner_protected',
    'share_download_disabled',
    'share_comment_disabled',
    'origin_mismatch',
    'tag_creation_restricted',
    'share_identity_required',
  ],
  404: ['not_found', 'embedding_missing'],
  409: [
    'conflict',
    'email_taken',
    'slug_taken',
    'idempotency_conflict',
    'role_is_system',
    'role_in_use',
    'library_not_empty',
    'version_is_active',
    'version_not_ready',
    'parts_incomplete',
    'upload_not_open',
    'already_resolved',
    'not_deleted',
    'board_is_smart',
    'asset_incomplete',
    'preset_in_use',
    'guest_only_role',
    'board_is_private',
  ],
  410: ['share_link_expired', 'share_link_revoked', 'form_inactive', 'form_expired'],
  413: ['quota_exceeded', 'file_too_large'],
  415: ['mime_not_allowed'],
  422: [
    'validation_failed',
    'board_cycle',
    'board_depth_exceeded',
    'cover_not_in_subtree',
    'reply_depth_exceeded',
    'frame_index_mismatch',
    'geometry_out_of_bounds',
    'too_many_points',
    'field_type_mismatch',
    'tag_limit_reached',
    'custom_field_limit_reached',
    'transform_unsupported',
  ],
  429: ['rate_limited'],
  500: ['internal_error'],
  501: ['not_implemented'],
  503: ['not_ready', 'service_unavailable'],
} as const;

export type ErrorStatus = keyof typeof ERROR_CODES_BY_STATUS;
export type ErrorCode = (typeof ERROR_CODES_BY_STATUS)[ErrorStatus][number];

/** Enum-style object: `ErrorCode.permission_denied === 'permission_denied'`. */
export const ErrorCode = Object.freeze(
  Object.fromEntries(
    Object.values(ERROR_CODES_BY_STATUS).flatMap((codes) => codes.map((c) => [c, c])),
  ) as { [K in ErrorCode]: K },
);

export const ERROR_CODES = Object.keys(ErrorCode) as readonly ErrorCode[];

const statusByCode = new Map<string, ErrorStatus>();
for (const [status, codes] of Object.entries(ERROR_CODES_BY_STATUS)) {
  for (const c of codes) statusByCode.set(c, Number(status) as ErrorStatus);
}

export function httpStatusForCode(code: ErrorCode): ErrorStatus {
  return statusByCode.get(code) ?? 500;
}

export function isErrorCode(value: unknown): value is ErrorCode {
  return typeof value === 'string' && statusByCode.has(value);
}

/** Code-specific `details` shapes documented in api-conventions.md. */
export interface ValidationFieldIssue {
  path: string;
  code: string;
  message: string;
}
export interface ErrorDetailsByCode {
  validation_failed: { fields: ValidationFieldIssue[] };
  permission_denied: { required_mask: number; effective_mask: number; missing_bits?: string[] };
  parts_incomplete: { missing_parts: number[] };
  quota_exceeded: { quota_bytes: number; used_bytes: number; requested_bytes: number };
}

export type ErrorDetails<C extends ErrorCode> = C extends keyof ErrorDetailsByCode
  ? ErrorDetailsByCode[C]
  : Record<string, unknown> | undefined;

/** Uniform wire envelope (`ErrorResponse` component). */
export interface ApiErrorBody<C extends ErrorCode = ErrorCode> {
  code: C;
  message: string;
  details?: ErrorDetails<C>;
  request_id: string;
}

/**
 * Throwable error carrying a stable code. The api maps it to `ErrorResponse`; the web client
 * re-hydrates responses into it.
 */
export class ApiError<C extends ErrorCode = ErrorCode> extends Error {
  readonly code: C;
  readonly status: ErrorStatus;
  readonly details: ErrorDetails<C> | undefined;
  readonly requestId: string | undefined;

  constructor(
    code: C,
    message?: string,
    options?: { details?: ErrorDetails<C>; requestId?: string; cause?: unknown },
  ) {
    super(message ?? code, options?.cause !== undefined ? { cause: options.cause } : undefined);
    this.name = 'ApiError';
    this.code = code;
    this.status = httpStatusForCode(code);
    this.details = options?.details;
    this.requestId = options?.requestId;
  }

  toBody(requestId: string = this.requestId ?? ''): ApiErrorBody<C> {
    const body: ApiErrorBody<C> = { code: this.code, message: this.message, request_id: requestId };
    if (this.details !== undefined) body.details = this.details;
    return body;
  }

  /** Re-hydrate from a wire body; unknown codes degrade to `internal_error`. */
  static fromBody(body: {
    code: string;
    message?: string;
    details?: unknown;
    request_id?: string;
  }): ApiError {
    const code: ErrorCode = isErrorCode(body.code) ? body.code : ErrorCode.internal_error;
    return new ApiError(code, body.message, {
      details: body.details as ErrorDetails<ErrorCode>,
      requestId: body.request_id,
    });
  }
}

export function isApiError(e: unknown): e is ApiError {
  return (
    e instanceof ApiError ||
    (typeof e === 'object' &&
      e !== null &&
      (e as { name?: unknown }).name === 'ApiError' &&
      isErrorCode((e as { code?: unknown }).code))
  );
}
